import { FieldGroup, SelectField, TextField } from "../../ui/fields";
import { fieldGridClass } from "./parts";
import { WALL_TIME_MESSAGES, type WallTimeFailure, wallTimeToInstant } from "../wall-time";

// A time the staff member types: a date, a wall time and an EXPLICIT IANA zone (contract 3.4). The zone field starts from the zone of the
// request (the venue's zone) when it has one, and is never taken from the browser. A time that does not exist is refused; a time that
// happens twice asks which one. The result is an instant the API takes as ISO 8601.

export type WallTimeState = Readonly<{
  date: string;
  time: string;
  zone: string;
  choice: "" | "earlier" | "later";
}>;

export const emptyWallTime = (zone: string | null): WallTimeState => ({
  date: "",
  time: "",
  zone: zone ?? "",
  choice: "",
});

export type WallTimeError = Readonly<{
  field: "date" | "time" | "zone" | "choice";
  message: string;
}>;

export type ResolvedWallTime =
  | Readonly<{ ok: true; instant: string | null }>
  | Readonly<{ ok: false; error: WallTimeError }>;

const FIELD_OF: Readonly<Record<WallTimeFailure, WallTimeError["field"]>> = {
  FORMAT: "date",
  ZONE_UNKNOWN: "zone",
  NOT_EXISTENT: "time",
  AMBIGUOUS: "choice",
};

/** An optional time: both date and time empty is "no time". Anything else must convert. */
export function resolveOptionalWallTime(state: WallTimeState): ResolvedWallTime {
  if (state.date.trim() === "" && state.time.trim() === "") return { ok: true, instant: null };
  const result = wallTimeToInstant({
    date: state.date.trim(),
    time: state.time.trim(),
    zone: state.zone,
    choice: state.choice === "" ? undefined : state.choice,
  });
  if (result.ok) return { ok: true, instant: result.instant };
  return {
    ok: false,
    error: { field: FIELD_OF[result.reason], message: WALL_TIME_MESSAGES[result.reason] },
  };
}

type Props = Readonly<{
  idPrefix: string;
  legend: string;
  hint?: string;
  state: WallTimeState;
  onChange: (state: WallTimeState) => void;
  error: WallTimeError | undefined;
}>;

export function WallTimeFields({ idPrefix, legend, hint, state, onChange, error }: Props) {
  // The choice shows as soon as the typed time is ambiguous, so the staff member sees why.
  const live =
    state.date.trim() !== "" && state.time.trim() !== ""
      ? wallTimeToInstant({ date: state.date.trim(), time: state.time.trim(), zone: state.zone })
      : null;
  const ambiguous =
    (live !== null && !live.ok && live.reason === "AMBIGUOUS") || error?.field === "choice";
  const on = (field: WallTimeError["field"]) =>
    error?.field === field ? error.message : undefined;
  return (
    <FieldGroup legend={legend} hint={hint}>
      <div className={fieldGridClass}>
        <TextField
          id={`${idPrefix}-date`}
          label={`${legend}: date`}
          hint="YYYY-MM-DD"
          placeholder="2026-10-18"
          value={state.date}
          onChange={(date) => onChange({ ...state, date, choice: "" })}
          error={on("date")}
        />
        <TextField
          id={`${idPrefix}-time`}
          label={`${legend}: time`}
          hint="HH:MM, 24 hour"
          placeholder="14:30"
          value={state.time}
          onChange={(time) => onChange({ ...state, time, choice: "" })}
          error={on("time")}
        />
        <TextField
          id={`${idPrefix}-zone`}
          label={`${legend}: time zone`}
          hint="An IANA name, for example Europe/London"
          placeholder="Europe/London"
          value={state.zone}
          onChange={(zone) => onChange({ ...state, zone, choice: "" })}
          error={on("zone")}
        />
      </div>
      {ambiguous ? (
        <SelectField
          id={`${idPrefix}-choice`}
          label={`${legend}: which one?`}
          hint="The clocks go back, so this time happens twice."
          value={state.choice}
          onChange={(choice) => onChange({ ...state, choice: choice as WallTimeState["choice"] })}
          options={[
            { value: "earlier", label: "The earlier one (before the clocks go back)" },
            { value: "later", label: "The later one (after the clocks go back)" },
          ]}
          placeholder="Choose"
          error={on("choice")}
        />
      ) : null}
    </FieldGroup>
  );
}
